import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * Mảnh dùng lại trong màn admin.
 *
 * Năm mục đều là "một tấm nổi, có đầu đề, ruột cuộn được" — viết một lần ở đây
 * thì năm chỗ không lệch nhau về lề và nhịp.
 */

export function Khung({
  tieuDe,
  phu,
  hanhDong,
  children,
}: {
  tieuDe: string;
  phu?: React.ReactNode;
  hanhDong?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div
      className="noi troi-len"
      style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      <div
        style={{
          padding: '18px 22px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          flexShrink: 0,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 'var(--chu-to)', fontWeight: 700 }}>{tieuDe}</div>
          {phu && (
            <div style={{ fontSize: 12, color: 'var(--chu-3)', marginTop: 4, lineHeight: 1.6 }}>
              {phu}
            </div>
          )}
        </div>
        {hanhDong && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, flexShrink: 0 }}>
            {hanhDong}
          </div>
        )}
      </div>
      {/* Lề 18px: bóng của mỗi dòng trải ra ~19px, dán sát mép thì bị cắt cụt. */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '4px 18px 18px' }}>
        {children}
      </div>
    </div>
  );
}

/** Dải lỗi — giếng lõm, chữ màu nhấn. Luôn hiện NGUYÊN câu của server. */
export function DaiLoi({ loi }: { loi: string | null }) {
  if (!loi) return null;
  return (
    <div
      role="alert"
      className="lom-nho hien-len"
      style={{
        padding: '12px 16px',
        marginBottom: 14,
        color: 'var(--nhan-dam)',
        fontSize: 'var(--chu-nho)',
        fontWeight: 600,
      }}
    >
      {loi}
    </div>
  );
}

export function DaiTot({ chu }: { chu: string | null }) {
  if (!chu) return null;
  return (
    <div
      className="lom-nho hien-len"
      style={{
        padding: '12px 16px',
        marginBottom: 14,
        color: 'var(--tot)',
        fontSize: 'var(--chu-nho)',
        fontWeight: 600,
      }}
    >
      {chu}
    </div>
  );
}

export function O({ nhan, children }: { nhan: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block', marginBottom: 16 }}>
      <span className="nhan-chu">{nhan}</span>
      <div style={{ marginTop: 7 }}>{children}</div>
    </label>
  );
}

/**
 * Hộp hỏi lại.
 *
 * 🔴 Treo thẳng vào `document.body` bằng portal. Các tấm admin đều có
 *    `overflow: hidden` để nội dung không tràn ra góc bo tròn, mà theo quy chuẩn
 *    `overflow` khác `visible` tạo ra **backdrop root** — để hộp thoại bên trong
 *    thì `backdrop-filter` chỉ làm mờ được phần nền nằm trong tấm đó. Đã nhìn
 *    thấy thật ở màn thu ngân: nửa màn hình mờ, nửa còn lại nét.
 */
/**
 * Ngăn xếp hộp thoại đang mở.
 *
 * 🔴 Cần vì hộp có thể LỒNG NHAU: hộp "Thêm sản phẩm" mở tiếp hộp "Thêm nhóm
 *    mới". Cả hai đều nghe `keydown` trên `window`, nên một lần bấm Escape sẽ
 *    đóng CẢ HAI — người dùng chỉ định thoát cái trên cùng thì mất luôn cả form
 *    đang điền dở, mà không có gì báo là vừa mất.
 *
 *    Chỉ hộp nằm cuối ngăn xếp mới phản ứng với Escape. Không cần lo z-index:
 *    portal nối vào `document.body` theo thứ tự mount, nên hộp mở sau nằm sau
 *    trong DOM và tự vẽ đè lên.
 */
const NGAN_XEP: symbol[] = [];

export function HopHoi({
  tieuDe,
  children,
  nutY,
  nguyHiem,
  dangLam,
  dong,
  dongY,
  choPhep = true,
  anQuayLai,
}: {
  tieuDe: string;
  children: React.ReactNode;
  nutY: string;
  nguyHiem?: boolean;
  dangLam: boolean;
  dong: () => void;
  dongY: () => void;
  choPhep?: boolean;
  /**
   * Ẩn nút "Quay lại".
   *
   * Dành cho hộp KHÔNG hỏi đồng ý gì cả — như hộp quản lý nhóm, nơi mọi thay đổi
   * đã lưu ngay lúc bấm. Ở đó hai nút "Quay lại" và "Xong" làm cùng một việc, mà
   * đặt cạnh nhau thì đọc ra như "huỷ" với "lưu" — sai hẳn nghĩa.
   */
  anQuayLai?: boolean;
}) {
  // Ghi tên mình vào ngăn xếp đúng một lần cho cả đời hộp. Tách khỏi effect
  // nghe phím bên dưới vì effect đó chạy lại mỗi khi `dong` hay `dangLam` đổi —
  // gộp chung thì mỗi lần chạy lại là một lần đẩy thêm vào ngăn xếp.
  const the = useRef<symbol | null>(null);
  if (the.current === null) the.current = Symbol('hop-hoi');

  useEffect(() => {
    const t = the.current!;
    NGAN_XEP.push(t);
    return () => {
      const i = NGAN_XEP.indexOf(t);
      if (i >= 0) NGAN_XEP.splice(i, 1);
    };
  }, []);

  useEffect(() => {
    const f = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || dangLam) return;
      // Không phải hộp trên cùng thì im: hộp lồng bên trên đang giữ Escape.
      if (NGAN_XEP[NGAN_XEP.length - 1] !== the.current) return;
      dong();
    };
    window.addEventListener('keydown', f);
    return () => window.removeEventListener('keydown', f);
  }, [dong, dangLam]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      onClick={dong}
      className="hien-mo"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(126, 120, 128, 0.46)',
        backdropFilter: 'blur(4px)',
        display: 'grid',
        placeItems: 'center',
        padding: 20,
        zIndex: 50,
      }}
    >
      {/*
        🔴 Hàng nút GHIM ở đáy, chỉ phần ruột cuộn.
 
           Hộp "Tạo chương trình giảm giá" cao hơn màn hình 900px — chiều cao
           laptop rất thường gặp. Bản đầu cho cả hộp cuộn, và nút TẠO nằm dưới
           mép: quản lý điền xong cả form mà không thấy nút đâu. Chỉ lộ ra khi
           chụp màn hình thật.
      */}
      <div
        className="noi phong-len"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 480,
          boxShadow: 'var(--noi-to)',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div style={{ fontSize: 20, fontWeight: 700, padding: '28px 30px 12px', flexShrink: 0 }}>
          {tieuDe}
        </div>
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            // Lề 4px hai bên trong vùng cuộn: bóng của các giếng lõm trải ra
            // ngoài mép, dán sát thì bị cắt cụt.
            padding: '4px 30px 20px',
            color: 'var(--chu-2)',
            lineHeight: 1.65,
          }}
        >
          {children}
        </div>
        {/*
          Bóng đổ NGƯỢC LÊN: hàng nút phải đọc ra là một lớp nổi trên nội dung
          đang cuộn bên dưới. Không có nó thì phần bị cắt trông như lỗi hiển thị
          chứ không như "còn nữa, cuộn xuống đi" — mà ở phong cách này không được
          phép kẻ một đường ngang để phân tách.
        */}
        <div
          style={{
            display: 'flex',
            gap: 12,
            padding: '14px 30px 26px',
            flexShrink: 0,
            boxShadow: '0 -10px 14px -10px var(--toi)',
          }}
        >
          {!anQuayLai && (
            <button style={{ flex: 1, padding: 14 }} onClick={dong} disabled={dangLam}>
              Quay lại
            </button>
          )}
          <button
            className={nguyHiem ? 'nut-nguy' : 'nut-chinh'}
            style={{ flex: 1, padding: 14 }}
            onClick={dongY}
            disabled={dangLam || !choPhep}
          >
            {dangLam ? 'Đang làm…' : nutY}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
